import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { isFunction, isNonEmptyString } from "@/utils";

export type AlertDialogMessageSeverity =
  | "info"
  | "success"
  | "warning"
  | "error";

export type AlertDialogProps = {
  message?: string;
  title?: string;
  confirmText?: string;
  severity?: AlertDialogMessageSeverity;
  onCloseCallback?: () => void;
};

export const AlertDialog: React.FC<AlertDialogProps> = ({
  message,
  title,
  confirmText,
  severity,
  onCloseCallback,
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(isNonEmptyString(message));
  }, [message]);

  const handleClose = () => {
    setOpen(false);
    if (isFunction(onCloseCallback)) {
      onCloseCallback?.();
    }
  };

  const getSeverityStyles = () => {
    switch (severity) {
      case "error":
        return { color: "#d32f2f" };
      case "warning":
        return { color: "#ed6c02" };
      case "success":
        return { color: "#2e7d32" };
      case "info":
      default:
        return { color: "#0288d1" };
    }
  };

  if (!message) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      {isNonEmptyString(title) ? (
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            ...getSeverityStyles(),
            fontWeight: "bold",
          }}
        >
          {title}
        </DialogTitle>
      ) : null}

      <DialogContent>
        <Typography
          id="alert-dialog-description"
          sx={{ color: "text.primary" }}
        >
          {message}
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            backgroundColor: getSeverityStyles().color,
            "&:hover": {
              backgroundColor: getSeverityStyles().color,
              opacity: 0.9,
            },
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AlertDialog.displayName = "AlertDialog";

export default AlertDialog;
